import React, {
  useEffect,
  ComponentProps,
  ReactNode,
  ComponentType,
} from 'react';

import { noop } from '@adsk/alloy-react-helpers';
import {
  LocaleProvider,
  supportedLocales,
  useLocale,
} from '@adsk/alloy-react-locale';
import { NotificationsProvider } from '@adsk/alloy-react-notification';
import { Reset } from '@adsk/alloy-react-theme';

import { AccountEntitlementsProvider } from './AccountEntitlementsContext';
import { AlloyLaunchDarklyProvider } from './AlloyLaunchDarklyProvider';
import AlloyLaunchDarklySubscriber from './AlloyLaunchDarklySubscriber';
import { CompaniesProvider } from './CompaniesContext';
import { AUTO_FETCH_TYPES, AutoFetchType } from './consts';
import { CurrentAccountEntitlementsProvider } from './CurrentAccountEntitlementsContext';
import { CurrentAccountIdProvider } from './CurrentAccountIdContext';
import { CurrentProductIdProvider } from './CurrentProductIdContext';
import { CurrentProjectIdProvider } from './CurrentProjectIdContext';
import { CurrentToolIdProvider } from './CurrentToolIdContext';
import { CustomHelpProvider } from './CustomHelpLinkContext';
import { DeletedUsersProvider } from './DeletedUsers';
import {
  ProjectEntitlementsProvider,
  useProjectEntitlements,
  useUser,
} from './ProjectEntitlementsContext';
import { RolesProvider } from './RolesContext';
import { SubscriptionsConstructionProvider } from './SubscriptionsConstructionContext';
import { SubscriptionsProvider } from './SubscriptionsContext';
import { UserProvider } from './UserContext';
import { UsersProvider } from './UsersContext';

type RedirectError = {
  redirectUrl: string;
};

const getRedirectUrl = (error: unknown) => {
  if (
    error &&
    (error as Record<string, unknown>).hasOwnProperty('redirectUrl')
  ) {
    return (error as RedirectError).redirectUrl;
  }
  return undefined;
};

const CheckRedirectEntitlementsResponse = () => {
  const { error } = useProjectEntitlements();
  const redirectUrl = getRedirectUrl(error);
  useEffect(() => {
    if (redirectUrl) {
      console.error('AlloyProvider - redirecting to redirectUrl');
      window.location.assign(redirectUrl);
    }
  }, [redirectUrl]);
  return null;
};

const LocaleSetter = () => {
  const { user } = useUser();
  const localeFromEntitlements = user?.locale;
  const oxygenLocal = supportedLocales.find(
    (locale) => locale === localeFromEntitlements,
  );

  const { setLocale } = useLocale(noop);

  useEffect(() => {
    if (oxygenLocal && setLocale) {
      setLocale(oxygenLocal);
    }
  }, [oxygenLocal, setLocale]);

  return null;
};

type AlloyProviderProps = {
  projectId?: string;
  accountId?: string;
  productId?: ComponentProps<typeof CurrentProductIdProvider>['productId'];
  toolId?: ComponentProps<typeof CurrentToolIdProvider>['toolId'];
  autoFetchEntities?: AutoFetchType[];
  locale?: string;
  children?: ReactNode;
};

/**
 * AlloyProvider to mount all project data context providers.
 */
const AlloyProvider: ComponentType<AlloyProviderProps> = ({
  children,
  projectId,
  accountId,
  productId,
  toolId,
  autoFetchEntities = Object.values(AUTO_FETCH_TYPES),
  locale,
}) => (
  <AlloyLaunchDarklyProvider>
    <LocaleProvider locale={locale}>
      <Reset />
      <NotificationsProvider>
        <CurrentProductIdProvider productId={productId}>
          <CurrentToolIdProvider toolId={toolId}>
            <CurrentProjectIdProvider projectId={projectId}>
              <ProjectEntitlementsProvider>
                <CheckRedirectEntitlementsResponse />
                <CurrentAccountIdProvider accountId={accountId}>
                  <CurrentAccountEntitlementsProvider>
                    <AccountEntitlementsProvider
                      disabled={
                        !autoFetchEntities.includes(
                          AUTO_FETCH_TYPES.CURRENT_ACCOUNTS_ENTITLEMENTS,
                        )
                      }
                    >
                      <UserProvider disabled>
                        <AlloyLaunchDarklySubscriber />
                        <LocaleSetter />
                        <DeletedUsersProvider>
                          <UsersProvider>
                            <CompaniesProvider>
                              <RolesProvider>
                                <SubscriptionsProvider>
                                  <SubscriptionsConstructionProvider>
                                    <CustomHelpProvider>
                                      {children}
                                    </CustomHelpProvider>
                                  </SubscriptionsConstructionProvider>
                                </SubscriptionsProvider>
                              </RolesProvider>
                            </CompaniesProvider>
                          </UsersProvider>
                        </DeletedUsersProvider>
                      </UserProvider>
                    </AccountEntitlementsProvider>
                  </CurrentAccountEntitlementsProvider>
                </CurrentAccountIdProvider>
              </ProjectEntitlementsProvider>
            </CurrentProjectIdProvider>
          </CurrentToolIdProvider>
        </CurrentProductIdProvider>
      </NotificationsProvider>
    </LocaleProvider>
  </AlloyLaunchDarklyProvider>
);

AlloyProvider.displayName = 'AlloyProvider';

export default Object.assign(AlloyProvider, { AUTO_FETCH_TYPES });
